import { useCallback, useEffect, useMemo, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import type { AutoplayType } from "embla-carousel-autoplay";
import { cn } from "@githits/shared";
import type { ReplaySession } from "@githits/replay-shared";
import ExampleCard from "./ExampleCard";

const LANGUAGE_LABELS: Record<string, string> = {
  typescript: "TypeScript",
  python: "Python",
  rust: "Rust",
  cuda: "CUDA",
  go: "Go",
  elixir: "Elixir",
  java: "Java",
  csharp: "C#",
};

const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";
const AUTOPLAY_DELAY_MS = 8000;
// Embla supports per-breakpoint overrides but we drive the visible slide count
// via CSS flex-basis on each slide instead, which is simpler and responsive
// without needing JS to recompute.

interface CardCarouselProps {
  sessions: Record<string, ReplaySession>;
  viewedKeys: Set<string>;
  onSelect: (key: string) => void;
}

export default function CardCarousel({
  sessions,
  viewedKeys,
  onSelect,
}: CardCarouselProps) {
  const entries = Object.entries(sessions);
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Autoplay plugin — stable reference via useMemo so Embla doesn't re-init
  // on every render (which would break autoplay scheduling).
  const plugins = useMemo(
    () => [
      Autoplay({
        delay: AUTOPLAY_DELAY_MS,
        stopOnMouseEnter: true,
        stopOnFocusIn: true,
        stopOnInteraction: false,
      }),
    ],
    []
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      // "auto" makes Embla compute slidesToScroll from the flex-basis of each
      // slide — 25% basis on desktop = 4 slides per page, 100% on mobile = 1.
      slidesToScroll: "auto",
      duration: isDesktop ? 40 : 22, // Embla uses 1/25s units → ~40 ≈ 900ms, ~22 ≈ 500ms
    },
    plugins
  );

  // Re-init Embla when the viewport breakpoint changes so slidesToScroll picks
  // up the new flex-basis, then reset to the first slide.
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    emblaApi.scrollTo(0, true);
  }, [emblaApi, isDesktop]);

  // The autoplay plugin's stopOnMouseEnter only listens on the Embla viewport.
  // The chevron buttons are siblings of that viewport (so they can overflow
  // mobile), so hovering them wouldn't pause autoplay. Wire manual pause/resume
  // on the whole nav element via the plugin's public play/stop API.
  const getAutoplay = useCallback((): AutoplayType | undefined => {
    return emblaApi?.plugins()?.autoplay as AutoplayType | undefined;
  }, [emblaApi]);
  const pauseAutoplay = useCallback(() => {
    getAutoplay()?.stop();
  }, [getAutoplay]);
  const resumeAutoplay = useCallback(() => {
    getAutoplay()?.play();
  }, [getAutoplay]);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext();
  }, [emblaApi]);

  return (
    <nav
      className="ghw-relative ghw-flex ghw-items-center md:ghw-gap-2"
      aria-label="Replay demo questions"
      onMouseEnter={pauseAutoplay}
      onMouseLeave={resumeAutoplay}
    >
      {/* Left arrow — overlays the viewport on mobile, inline on desktop */}
      <button
        type="button"
        aria-label="Previous page"
        onClick={scrollPrev}
        className={cn(
          "ghw-flex ghw-shrink-0 ghw-items-center ghw-justify-center",
          "ghw-absolute ghw-left-[-16px] ghw-top-1/2 -ghw-translate-y-1/2 ghw-z-10",
          "md:ghw-static md:ghw-translate-y-0",
          "ghw-w-8 ghw-h-8 ghw-rounded-full ghw-border-none ghw-cursor-pointer",
          "ghw-bg-secondary ghw-text-foreground ghw-transition-colors",
          "hover:ghw-bg-secondary/80",
          "focus-visible:ghw-outline-none focus-visible:ghw-ring-2 focus-visible:ghw-ring-primary/40"
        )}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m15 18-6-6 6-6" />
        </svg>
      </button>

      {/* Embla viewport — keeps overflow:hidden for horizontal loop clipping.
          Vertical padding gives the 1.1 hover scale on cards room to grow
          without being clipped at the top/bottom edges, and horizontal padding
          gives the edge cards breathing room so they don't visually collide
          with the chevrons when scaled. */}
      <div className="ghw-flex-1 ghw-overflow-hidden md:ghw-py-4 md:ghw-px-3" ref={emblaRef}>
        {/* Embla container — negative left margin + per-slide left padding is
            the Embla-recommended pattern for creating visual gaps without
            CSS gap (which Embla's scroll math doesn't account for). */}
        <div className="ghw-flex ghw-touch-pan-y ghw--ml-3">
          {entries.map(([key, session]) => (
            <div
              key={key}
              className="ghw-min-w-0 ghw-shrink-0 ghw-grow-0 ghw-basis-full md:ghw-basis-1/4 ghw-pl-3"
            >
              <ExampleCard
                language={key}
                languageLabel={LANGUAGE_LABELS[key] ?? key}
                query={session.query.replace(/\s+/g, " ").trim()}
                isViewed={viewedKeys.has(key)}
                onCta={() => onSelect(key)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Right arrow — overlays the viewport on mobile, inline on desktop */}
      <button
        type="button"
        aria-label="Next page"
        onClick={scrollNext}
        className={cn(
          "ghw-flex ghw-shrink-0 ghw-items-center ghw-justify-center",
          "ghw-absolute ghw-right-[-16px] ghw-top-1/2 -ghw-translate-y-1/2 ghw-z-10",
          "md:ghw-static md:ghw-translate-y-0",
          "ghw-w-8 ghw-h-8 ghw-rounded-full ghw-border-none ghw-cursor-pointer",
          "ghw-bg-secondary ghw-text-foreground ghw-transition-colors",
          "hover:ghw-bg-secondary/80",
          "focus-visible:ghw-outline-none focus-visible:ghw-ring-2 focus-visible:ghw-ring-primary/40"
        )}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="m9 18 6-6-6-6" />
        </svg>
      </button>
    </nav>
  );
}
